import React, {useMemo, useContext} from 'react'
import PropTypes from 'prop-types'
import {BuilderContext} from '@builder.io/sdk-react'
import {
    breadcrumbTypes,
    generateBreadcrumbLinkContent,
    homePageData,
    getStandardBreadcrumbInputs
} from './utils'
import {useParentPageCrumbs} from './useParentPageCrumbs'
import {useCategoryCrumbs} from '../../hooks/useCategoryCrumbs'
import {useAutoCategoryBreadcrumbs} from '../../hooks/useAutoCategoryBreadcrumbs'
import {BuilderBreadcrumb} from './breadcrumb'

export const StandardBreadcrumb = (props) => {
    const {
        customCrumbs = [],
        categoryCrumbs = [],
        type,
        separator,
        builderBlock,
        attributes,
        includeHome = true
    } = props

    // Extract category ID from Builder context for auto-breadcrumbs
    const builderContext = useContext(BuilderContext)
    const categoryId = builderContext?.content?.data?.productCategory?.options?.category

    const {parentPageCrumbs} = useParentPageCrumbs({
        isParentPageType: type === breadcrumbTypes.PARENT_PAGE_REFERENCE
    })

    const {categoryCrumbs: formattedCategoryCrumbs} = useCategoryCrumbs({
        categoryCrumbs,
        isEnabled: type === breadcrumbTypes.CATEGORY_MANUAL
    })

    const {categoryCrumbs: autoCategoryCrumbs} = useAutoCategoryBreadcrumbs({
        isEnabled: type === breadcrumbTypes.CATEGORY,
        categoryId
    })

    const crumbs = useMemo(() => {
        let breadcrumbs = []

        switch (type) {
            case breadcrumbTypes.PARENT_PAGE_REFERENCE:
                breadcrumbs = parentPageCrumbs
                break
            case breadcrumbTypes.CUSTOM:
                breadcrumbs = customCrumbs
                    .filter(({pageReference}) => !!pageReference)
                    .map(({label, isClickable, pageReference}) =>
                        generateBreadcrumbLinkContent({
                            title: label,
                            isClickable,
                            pageData: pageReference
                        })
                    )
                break
            case breadcrumbTypes.CATEGORY_MANUAL:
                // Use manual categoryCrumbs if provided, otherwise use auto-generated
                breadcrumbs = categoryCrumbs?.length ? formattedCategoryCrumbs : autoCategoryCrumbs
                break
            case breadcrumbTypes.CATEGORY:
                breadcrumbs = autoCategoryCrumbs
                break
            default:
                breadcrumbs = []
        }

        // Add home if includeHome is true and the first crumb isn't already home
        if (
            includeHome &&
            breadcrumbs.length > 0 &&
            (!breadcrumbs[0]?.component?.options?.pageReference?.value?.data?.url === '/' ||
                breadcrumbs[0]?.component?.options?.label !== 'Home')
        ) {
            return [homePageData, ...breadcrumbs]
        }

        return breadcrumbs
    }, [
        type,
        includeHome,
        parentPageCrumbs,
        customCrumbs,
        categoryCrumbs?.length,
        formattedCategoryCrumbs,
        autoCategoryCrumbs
    ])

    return (
        <BuilderBreadcrumb
            crumbs={crumbs}
            builderBlock={builderBlock}
            attributes={attributes}
            separator={separator}
            data-testid="standardBreadcrumb-BuilderBreadcrumb"
        />
    )
}

StandardBreadcrumb.propTypes = {
    attributes: PropTypes.object,
    separator: PropTypes.node,
    type: PropTypes.string,
    includeHome: PropTypes.bool,
    customCrumbs: PropTypes.arrayOf(PropTypes.object),
    categoryCrumbs: PropTypes.arrayOf(PropTypes.object),
    builderBlock: PropTypes.object
}

export const StandardBreadcrumbDefinition = {
    component: StandardBreadcrumb,
    name: 'Breadcrumb',
    canHaveChildren: true,
    childRequirements: {
        message: "Children of 'Breadcrumb' must be 'BreadcrumbLink'",
        query: {
            'component.name': {$in: ['BreadcrumbLink']}
        }
    },
    noWrap: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    inputs: getStandardBreadcrumbInputs()
}
